import { useEffect, useState } from "react";

let cache = null;
let pending = null;

function load() {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = fetch("/api/ranking")
      .then((res) => {
        if (!res.ok) throw new Error("ranking");
        return res.json();
      })
      .then((body) => {
        cache = Array.isArray(body?.schools) ? body.schools : [];
        return cache;
      })
      .catch((err) => {
        pending = null;
        throw err;
      });
  }
  return pending;
}

/** Latest grammar-school rows already used by the ranking page. */
export function useGrammarBenchmarks(enabled) {
  const [rows, setRows] = useState(cache);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    if (cache) {
      setRows(cache);
      return undefined;
    }
    let cancel = false;
    load()
      .then((list) => {
        if (!cancel) setRows(list);
      })
      .catch(() => {
        if (!cancel) setError(true);
      });
    return () => {
      cancel = true;
    };
  }, [enabled]);

  return { rows, loading: enabled && !rows && !error, error };
}

/**
 * Mean, low and high among grammar schools.
 * Prefer the same cohort year. If fewer than 8 schools have that year, use each school’s latest stored year.
 */
export function grammarBenchmark(rows, yearKey, valueKey, year) {
  if (!rows?.length) return null;
  const finite = (school) => {
    const n = Number(school?.[valueKey]);
    return Number.isFinite(n) ? n : null;
  };
  const same = rows.filter((school) => Number(school?.[yearKey]) === Number(year) && finite(school) != null);
  const pool = same.length >= 8 ? same : rows.filter((school) => finite(school) != null);
  if (!pool.length) return null;
  const values = pool.map(finite);
  const sum = values.reduce((total, n) => total + n, 0);
  return {
    mean: sum / values.length,
    min: Math.min(...values),
    max: Math.max(...values),
    count: values.length,
    sameYear: same.length >= 8,
  };
}
